/**
 * Tools editing card (P4.T4).
 *
 * Replaces the T1 placeholder body. Renders one row per installed
 * tool binding sorted by capability_id with:
 *   - capability label + category badge + description (from catalog)
 *   - inline rebind <select> (REBIND affordance — same Select pattern
 *     as the admin PersonaToolBindings BindingRow)
 *   - adapter-status text ("bound to <display_name>" or
 *     "unbound — adapter <id> not registered") — forward-promise DOM
 *     for P4.T9 traffic-light overlay (no rework required)
 *   - credential-set text (read-only display; no editing)
 *   - remove button (UNBIND affordance)
 *
 * "+ Add tool" button at the bottom opens the ToolBindingPickerModal
 * (ADD affordance). Modal = ADD, Select = REBIND, X = UNBIND — three
 * disjoint affordances.
 *
 * Reads:
 *   - draftBundle ?? serverBundle for installed bindings
 *   - useCapabilities (P2.T2) for capability display labels + categories
 *   - useAdapters (P2.T4) for adapter display names + compatibility
 *
 * Writes via composerStore.bindTool / unbindTool. Save flows through
 * the Workshop top bar (T2) → useUpdatePersonaBundle (T2,
 * widened in T4 to send tool_bindings).
 *
 * Catalog-load failure: card still renders installed bindings (with
 * bare capability_id labels — no display lookup); inline Alert at the
 * top surfaces the error with a Retry button; rebind Select is disabled
 * when the ADAPTER catalog fails.
 *
 * No `policy_overrides` editing (preserved across rebind by
 * composerStore.bindTool; editing surface is Power Editor P4.T6 + admin).
 * No `credential_set_id` picker (read-only display; future ticket adds
 * the picker on both surfaces simultaneously). See plan §5 pins.
 */
import { useState, useMemo } from 'react'
import { X } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { useComposerStore, type ToolBindingValue } from '@/stores/composerStore'
import { useCapabilities } from '@/hooks/useCapabilities'
import { useAdapters } from '@/hooks/useAdapters'
import { useBundleFindings } from '@/hooks/useBundleFindings'
import { useFeature } from '@/hooks/useFeature'
import { usePersonaToolBindings } from '@/hooks/useToolBindings'
import type { Capability, Adapter, ResolvedToolBindingEntry } from '@/types/toolFramework'
import { TrafficLight } from '@/components/composer/TrafficLight'
import { ExplainPopover } from '@/components/composer/ExplainPopover'
import type { Finding } from '@/utils/bundleFindings'
import { ToolBindingPickerModal } from './ToolBindingPickerModal'
import { adapterNeedsCredentials } from '@/utils/adapterCredentials'

function healthLabel(s: ResolvedToolBindingEntry['connection_status']): string {
  return s === 'connected' ? 'healthy'
    : s === 'expired' ? 'reconnect needed'
    : s === 'error' ? 'error'
    : 'not connected'
}

interface BindingRowProps {
  capability_id: string
  binding: ToolBindingValue
  capability: Capability | undefined
  boundAdapter: Adapter | undefined
  compatibleAdapters: Adapter[]
  adaptersDisabled: boolean
  finding: Finding | undefined
  provenance?: ResolvedToolBindingEntry
}

function BindingRow({
  capability_id,
  binding,
  capability,
  boundAdapter,
  compatibleAdapters,
  adaptersDisabled,
  finding,
  provenance,
}: BindingRowProps) {
  const bindTool = useComposerStore((s) => s.bindTool)
  const unbindTool = useComposerStore((s) => s.unbindTool)

  // If the currently-bound adapter isn't in the compatible list (either
  // adapter not in catalog, or catalog hasn't loaded), render it as an
  // extra option so the Select shows the current value rather than
  // silently shifting to the first compatible option.
  const showCurrentAsFallback = !compatibleAdapters.some(
    (a) => a.id === binding.adapter_id,
  )

  return (
    <li className="rounded-sm border border-border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-medium text-foreground">
              {capability_id}
            </span>
            {capability && (
              <Badge variant="secondary" className="shrink-0">
                {capability.category}
              </Badge>
            )}
          </div>
          {capability?.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {capability.description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => unbindTool(capability_id)}
          aria-label={`Remove binding for ${capability_id}`}
          className="shrink-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div>
        <label
          htmlFor={`adapter-select-${capability_id}`}
          className="text-xs text-muted-foreground block mb-1"
        >
          Adapter for {capability_id}
        </label>
        <select
          id={`adapter-select-${capability_id}`}
          aria-label={`Adapter for ${capability_id}`}
          value={binding.adapter_id}
          onChange={(e) => bindTool(capability_id, e.target.value)}
          disabled={adaptersDisabled}
          title={
            adaptersDisabled
              ? 'Adapter catalog unreachable — retry above'
              : undefined
          }
          className="w-full rounded-md border border-border px-2 py-1 text-sm bg-background disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {compatibleAdapters.map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name} ({a.kind})
            </option>
          ))}
          {showCurrentAsFallback && (
            <option value={binding.adapter_id}>
              {binding.adapter_id} (current)
            </option>
          )}
        </select>
      </div>

      <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <span>
          {boundAdapter
            ? `bound to ${boundAdapter.display_name}`
            : `unbound — adapter ${binding.adapter_id} not registered`}
        </span>
        {finding && (
          // P4.T9: Tools-card findings (rules 3+4+5) have no jumpTo — the
          // user fixes them on the same card the popover renders on.
          <ExplainPopover title={finding.message} reasoning={finding.explanation}>
            <button
              type="button"
              className="cursor-pointer ml-1 align-middle"
              aria-label={`Explain finding for ${capability_id}`}
            >
              <TrafficLight severity={finding.severity} />
            </button>
          </ExplainPopover>
        )}
      </p>

      {adapterNeedsCredentials(boundAdapter, capability) ? (
        provenance ? (
          <p className="text-[11px] text-muted-foreground">
            {provenance.source === 'explicit'
              ? `${provenance.effective_credential_set_name ?? 'credential set'} (explicit)`
              : provenance.source === 'deployment'
                ? `deployment connection (${healthLabel(provenance.connection_status)})`
                : '⚠ no credentials'}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            {binding.credential_set_id ? 'Credential set bound' : 'Uses default credential set'}
          </p>
        )
      ) : boundAdapter ? (
        <p className="text-[11px] text-muted-foreground">Built-in · no key needed</p>
      ) : null}
    </li>
  )
}

export function ToolsCard() {
  const [pickerOpen, setPickerOpen] = useState(false)
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const personaId = useComposerStore((s) => s.personaId) ?? ''
  // Self-gate: the capabilities/adapters/tool-bindings routers only
  // register under tool_framework. On deployments without it (e.g.
  // enterprise + persona_workshop delta) this card renders nothing and
  // fires nothing — covers both the Composer page and the Workshop's
  // advanced Capabilities section in one place. All hooks still run
  // unconditionally (rules of hooks); the flag only disables queries
  // and short-circuits the render below.
  const toolFrameworkOn = useFeature('tool_framework')
  const caps = useCapabilities({ enabled: toolFrameworkOn })
  const adapters = useAdapters({ enabled: toolFrameworkOn })
  const findings = useBundleFindings()
  const { data: resolved } = usePersonaToolBindings(personaId, {
    enabled: toolFrameworkOn,
  })
  const provenanceByCap = useMemo(() => {
    const m: Record<string, ResolvedToolBindingEntry> = {}
    for (const b of resolved?.bindings ?? []) m[b.capability_id] = b
    return m
  }, [resolved])

  const base = draft ?? server
  const tool_bindings = base?.tool_bindings ?? {}

  const capabilityMap = useMemo(
    () => new Map<string, Capability>((caps.data ?? []).map((c) => [c.id, c])),
    [caps.data],
  )
  const adapterMap = useMemo(
    () => new Map<string, Adapter>((adapters.data ?? []).map((a) => [a.id, a])),
    [adapters.data],
  )

  const sortedBindings = useMemo(() => {
    const entries = Object.entries(tool_bindings) as [string, ToolBindingValue][]
    return entries.sort(([a], [b]) => a.localeCompare(b))
  }, [tool_bindings])

  // P4.T9: anchorId for tools findings is the capability_id (the key of
  // tool_bindings — matches the rule engine's emit shape).
  const findingForBinding = (cap: string): Finding | undefined =>
    findings.find(
      (f) => f.location.card === 'tools' && f.location.anchorId === cap,
    )

  const hasError = caps.isError || adapters.isError

  if (!toolFrameworkOn) return null

  return (
    <Card className="p-6 gap-0">
      <h2 className="text-lg font-semibold mb-2">Tools</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Capabilities (accept payments, find slots, send messages) bound to specific providers.
        Each capability picks one adapter.
      </p>

      {hasError && (
        <Alert variant="destructive" className="text-xs mb-4">
          <div className="flex items-center justify-between gap-3">
            <span>
              Unable to load the tool catalog — display names + the rebind dropdown may be limited.
              Installed-binding removal still works.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                caps.refetch()
                adapters.refetch()
              }}
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {sortedBindings.length === 0 ? (
        <p className="text-sm text-muted-foreground italic mb-4">
          No tools bound. Click + Add tool to choose a capability and adapter.
        </p>
      ) : (
        <ul className="space-y-3 mb-4">
          {sortedBindings.map(([cap_id, binding]) => (
            <BindingRow
              key={cap_id}
              capability_id={cap_id}
              binding={binding}
              capability={capabilityMap.get(cap_id)}
              boundAdapter={adapterMap.get(binding.adapter_id)}
              compatibleAdapters={(adapters.data ?? []).filter((a) =>
                a.implements.includes(cap_id),
              )}
              adaptersDisabled={adapters.isError}
              finding={findingForBinding(cap_id)}
              provenance={provenanceByCap[cap_id]}
            />
          ))}
        </ul>
      )}

      <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
        + Add tool
      </Button>

      <ToolBindingPickerModal open={pickerOpen} onOpenChange={setPickerOpen} />
    </Card>
  )
}
